import { FoodsSelect, FoodsTextInput, HelperBox } from "@foodpilot/foods";
import { Checkbox, FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TrajectoryForm, TrajectoryFormScoreOptions } from "./useTrajectoryForm";
import { GoalBlockList } from "./GoalBlock";
import { ScoreVariation } from "src/context/EntityBasedScore/ScoreService";
import { isCampaignBefore, useCampaigns } from "src/context/Campaigns/useCampaigns";
import { ElligibleStartingPost } from "src/context/Actions/context";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { Fragment } from "react/jsx-runtime";

type TrajectoryInitFormProps = {
    form: TrajectoryForm;
    setForm: (newValue: TrajectoryForm) => void;
    remainingScoreOptions: TrajectoryFormScoreOptions;
    allScoreOptions: ScoreVariation[];
    elligibleStartingPosts: ElligibleStartingPost[];
};
export const TrajectoryInitForm = (props: TrajectoryInitFormProps) => {
    const { form, setForm, remainingScoreOptions, allScoreOptions, elligibleStartingPosts } = props;

    const { isActionPlanSimplified } = useApplicationParameters();
    const theme = useTheme();
    const { t } = useTranslation();

    const selectedElligiblePost = elligibleStartingPosts.find((item) => item.id === form.startingPostId);

    const filterOptions =
        selectedElligiblePost?.campaign !== undefined ? { startFilter: selectedElligiblePost?.campaign } : {};

    const { campaigns: futureCampaigns } = useCampaigns({
        filterOptions: filterOptions,
    });

    const isSimplified = isActionPlanSimplified();
    const isAdvanced = isSimplified === false;

    return (
        <Stack flexDirection={"column"} gap={theme.spacing(4)} padding={theme.spacing(4)}>
            <Stack gap={theme.spacing(2)}>
                <Typography variant="h5">{t("actionPlan.trajectory.form.startingYear")}</Typography>
                <FoodsSelect
                    selectedOption={selectedElligiblePost ?? null}
                    options={elligibleStartingPosts}
                    onChange={(selectedElligiblePost) => {
                        const selectedId = selectedElligiblePost?.id;
                        if (selectedId === undefined) return;

                        const selectedStartingDate = selectedElligiblePost?.campaign.startDate ?? "";
                        const selectedEndDate = form.targetYear?.startDate ?? "";
                        const doesChoiceResetEnd = isCampaignBefore({
                            startDate: selectedEndDate,
                            endDate: selectedStartingDate,
                            canBeEqual: false,
                        });

                        setForm({
                            ...form,
                            targetYear: doesChoiceResetEnd ? null : form.targetYear,
                            startingPostId: selectedId,
                        });
                    }}
                    getId={(item) => item.id}
                    getName={(item) => item.campaign.label}
                    closeOnSelect
                    disableSearch
                    textOptions={{
                        placeholder: t("component.select.placeholder"),
                        searchPlaceholder: t("component.select.searchPlaceholder"),
                        emptyOptionsText: t("component.select.noOptions"),
                        defaultValueText: t("component.select.defaultValue"),
                        noResult: t("component.select.noResultFound"),
                    }}
                    BoxProps={{ width: "200px" }}
                />
                <HelperBox>
                    <Typography variant="body-medium">{t("actionPlan.trajectory.form.startingYear.hint")}</Typography>
                </HelperBox>
            </Stack>
            <Stack gap={theme.spacing(1)}>
                <Typography variant="h5">{t("actionPlan.trajectory.form.targetYear")}</Typography>
                {isSimplified && form.targetYear === null ?
                    <Stack gap={theme.spacing(1)}>
                        <HelperBox>
                            <Typography variant="body-medium">{t("action-plan.noTargetCampaign")}</Typography>
                        </HelperBox>
                    </Stack>
                :   <FoodsSelect
                        selectedOption={form.targetYear}
                        options={futureCampaigns}
                        onChange={(selectedCampaign) => {
                            setForm({
                                ...form,
                                targetYear: selectedCampaign,
                            });
                        }}
                        disabled={isSimplified}
                        getId={(campaign) => campaign.id}
                        getName={(campaign) => campaign.label}
                        closeOnSelect
                        disableSearch
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchPlaceholder"),
                            emptyOptionsText: t("component.select.noOptions"),
                            defaultValueText: t("component.select.defaultValue"),
                            noResult: t("component.select.noResultFound"),
                        }}
                        BoxProps={{ width: "200px" }}
                    />
                }
            </Stack>
            {isAdvanced && (
                <Fragment>
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="h5">{t("actionPlan.trajectory.form.growth")}</Typography>
                        <Stack flexDirection={"row"} gap={theme.spacing(4)}>
                            <FoodsTextInput
                                ExtraInputProps={{
                                    type: "number",
                                    sx: {
                                        maxWidth: "200px",
                                    },
                                }}
                                value={form.increasePerYear}
                                unit={"%"}
                                onChange={(newValue) => {
                                    setForm({
                                        ...form,
                                        increasePerYear: newValue,
                                    });
                                }}
                                disabled={form.isGrowthDisabled}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.isGrowthDisabled}
                                        onChange={(e) => {
                                            setForm({
                                                ...form,
                                                isGrowthDisabled: !form.isGrowthDisabled,
                                            });
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body-medium">
                                        {t("actionPlan.trajectory.form.noGrowthFactor")}
                                    </Typography>
                                }
                            />
                        </Stack>
                    </Stack>
                    <GoalBlockList
                        allScoreOptions={allScoreOptions}
                        remainingScoreOptions={remainingScoreOptions}
                        goals={form.reductionGoals}
                        setGoals={(newGoals) => {
                            setForm({
                                ...form,
                                reductionGoals: newGoals,
                            });
                        }}
                    />
                </Fragment>
            )}
        </Stack>
    );
};
